import {
    Injectable, OnDestroy
} from "@angular/core";
import {
    BehaviorSubject, Observable
} from "rxjs";
import {
    Language, Localized, ILocalizableModel
} from "@shopliftr/common-js/data";
import { AbstractListStateService } from "./abstract-list-state-service";
import { ILocalizedListStateService } from "../interfaces/localized-list-state-service";

/**
 * Abstract base class for list state services that manage rows of localizable models.
 *
 * Extends AbstractListStateService with language selection and on-the-fly row localization.
 * The third generic R is fixed to Localized<M> so that row-state methods accept both the
 * original model and its localized wrapper (unwrapping is handled by the base class via
 * ListUtil.unwrapRow).
 *
 * @template M - The model type (must implement ILocalizableModel)
 * @template O - The filter options type
 *
 * @example
 * ```typescript
 * @Injectable()
 * export class ProductListStateService
 *     extends AbstractLocalizedListStateService<Product, ProductFilterOptions> {
 *
 *     protected _getRowId(row: Product): string {
 *         return row.id;
 *     }
 * }
 * ```
 */
@Injectable()
export abstract class AbstractLocalizedListStateService<
    M extends ILocalizableModel & object,
    O extends object
>
    extends AbstractListStateService<M, O, Localized<M>>
    implements ILocalizedListStateService<M, O>, OnDestroy {

    private readonly _selectedLanguage$ = new BehaviorSubject<Language>(undefined);

    /**
     * The currently selected language, or undefined if no language is selected.
     */
    get selectedLanguage(): Language {
        return this._selectedLanguage$.value;
    }

    /**
     * Emits the current selected language and any subsequent changes.
     */
    get selectedLanguage$(): Observable<Language> {
        return this._selectedLanguage$.asObservable();
    }

    /**
     * Updates the selected language.
     *
     * Changing the language does not reset pagination or trigger a new filter request —
     * it is the caller's responsibility to refresh data after updating the language.
     *
     * @param language - The new language to select
     */
    updateSelectedLanguage(language: Language): void {
        this._selectedLanguage$.next(language);
    }

    /**
     * Returns a localized clone of the model for the currently selected language.
     * If no language is selected, returns the original model unchanged.
     *
     * @param model - The model to localize
     * @returns A localized version of the model, or the original model when no language is set
     */
    localizeRow(model: M): Localized<M> | M {
        return this.selectedLanguage ? model.localize(this.selectedLanguage) : model;
    }

    /**
     * Resets all list state and clears the selected language.
     */
    override reset(): void {
        super.reset();
        this._selectedLanguage$.next(undefined);
    }

    /**
     * Completes all internal subjects when the service is destroyed.
     */
    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this._selectedLanguage$.complete();
    }
}
